/**
 * Сводка по группам кошельков в панели «Накоплено на рынке».
 *
 * # Зачем блок
 *
 * Таблица отдаёт сотню крупнейших по направлению, и на вопрос оператора не
 * отвечает: «там десятки мелких кошелей на них сидит, это не так заметно
 * получается… большинство плюсовых разносит экшн на десятки кошелей». Накопление
 * размазано по хвосту, а хвост в сотню строк не попадает — на живом
 * пятиминутном окне рынок отторговывают 500–800 кошельков. Сводка считается по
 * ВСЕМ, и в этом вся её ценность.
 *
 * # Пять способов соврать этим блоком, и что с каждым сделано
 *
 *  1. ДВА ОКНА НА ОДНОМ ЭКРАНЕ. Группа назначается кошельку за всю накопленную
 *     глубину, а колонки разбивки по кошелькам — за окно переключателя. Разводятся
 *     трижды: заголовочной группой колонок, определением под именем группы и
 *     припиской, где оба окна названы РАЗНЫМИ значениями.
 *  2. ПОРОГ ОТБОРА. «Плюсовых 11» без порога — число ни о чём. Порог стоит на
 *     кнопке, в основании и в подсказке имени, а сама лестница приезжает с
 *     сервера: копии числа на этой стороне нет.
 *  3. ПОКРЫТИЕ КЛАССИФИКАЦИИ. Половина кошельков рынка не обойдена вовсе, и
 *     «плюсовые +1 480 Up» — утверждение о меньшинстве. Основание стоит ПЕРЕД
 *     числами и называет обе доли: по кошелькам и по обороту окна.
 *  4. ПОКРЫТИЕ ВЫПЛАТ. Знак группы включает выплаты, а спрошены они у малой
 *     части. Сказано числом и с НАПРАВЛЕНИЕМ ошибки: переводы входящие, значит
 *     плюсовых показано не больше, чем есть.
 *  5. ГЛУБИНА. Кошелёк с одним прибыльным днём весит столько же, сколько
 *     плюсовой месяц подряд. Медиана в колонке, счётчик «по одному обходу» под
 *     именем группы.
 *
 * # Почему отдельный модуль, а не дописка в participants.ts
 *
 * Не из-за длины. Шапка модуля в этом репозитории — основная документация, а
 * список выше не тот, что у таблицы: там пять СВОИХ способов соврать. Шестым
 * пунктом в чужом списке их перестанут читать целиком.
 *
 * Здесь же и всё форматирование: компонентных тестов в проекте нет вовсе, и всё,
 * что решает, КАКОЕ число видит оператор, обязано лежать под vitest.
 */
import type {
  ParticipantCohort,
  ParticipantCohortKind,
  ParticipantCohortSplit,
  ParticipantCohortToken,
  ParticipantCohorts,
  ParticipantsStats,
  Token,
} from "../api/types";
import { plural, shortId } from "./format";
import { add, div, fixed, mul, parse, roundToFixed, SCALE, type Micro } from "./money";
import { outcomeMap } from "./participants";
import { formatDollars, formatShares } from "./tape";

export type CohortKind = ParticipantCohortKind;
export type CohortSign = "plus" | "minus" | "rest";

/**
 * Знак подгруппы — разбором префикса имени, а не таблицей.
 *
 * Сервер строит имя как `знак_роль`, и это его контракт: единственное место,
 * где из подгруппы выводится знак, — там, у правила. Вторая таблица здесь
 * разошлась бы с первой на первом же новом имени, а разбор префикса — нет.
 * Незнакомый префикс — «остальные»: молчание не должно давать группу.
 */
export function signOf(kind: string): CohortSign {
  if (kind.startsWith("plus_")) return "plus";
  if (kind.startsWith("minus_")) return "minus";
  return "rest";
}

/** Плюсовые ТОРГОМ: пнл больше нуля и без выплат. Живущие на выплатах — нет. */
export function isTrading(kind: CohortKind): boolean {
  return kind === "plus_taker" || kind === "plus_maker" || kind === "plus_mixed";
}

/** Ступень лестницы порогов — та, что приехала, а не та, что мы придумали. */
export interface CohortFloor {
  /** Порог в долларах СТРОКОЙ: он же ключ выбора в сторе. */
  usd: string;
  /** Подпись кнопки: «$20 тыс.», «без порога». */
  label: string;
  /** Этим сервер считает, когда оператор не выбирал. */
  fallback: boolean;
}

/**
 * Одна строка сводки: группа × токен.
 *
 * Строка на ТОКЕН, а не сведённое направление, как у кошелька. Три довода, по
 * возрастанию тяжести:
 *
 *  1. Сведение уничтожает главный факт. Плюсовых двое: A набрал +1000 Up, B
 *     набрал +1000 Down. Сведение даёт ноль — «направления нет, средней нет», —
 *     а правда в том, что два плюсовых стоят друг против друга по 0.30 и 0.65.
 *     У ОДНОГО кошелька позиция на бинарном рынке действительно одномерна; у
 *     множества кошельков книги не схлопываются.
 *  2. У сведённого направления не бывает честной средней: «средняя между Up по
 *     0.2 и Down по 0.8 не значит ничего», и оператор просит именно среднюю.
 *  3. На небинарном рынке сводить нечего вовсе.
 *
 * Одно направление при этом никуда не делось — оно в колонке перевеса, то есть в
 * той единице, в которой сведение осмысленно.
 */
export interface CohortLine {
  kind: CohortKind;
  /** Ключ React: группа плюс токен. */
  key: string;
  /** Первая строка группы: только у неё рисуются левые ячейки и перевес. */
  head: boolean;
  /** Сколько строк у группы — значение rowSpan. */
  span: number;

  // --- левый блок: кто они, за всю накопленную глубину -------------------
  title: string;
  /** Определение под именем, ВИДИМОЕ: «пнл + выплаты > 0». */
  formula: string;
  titleHint: string;
  /** «не обходили 127 · ниже порога 87». Пусто у плюсовых и минусовых. */
  reasons: string;
  wallets: number;
  /**
   * Роли под именем: «тейкеров 1 · мейкеров 4». Только у живущих на выплатах —
   * у ролевых подгрупп роль в имени, у остальных она неприменима.
   */
  roles: string;
  rolesHint: string;
  /** «угадывают ≥60 %: 12 из 31 (≥20 окон)». Пусто — судить некого. */
  guessing: string;
  guessingHint: string;
  /** Доля побед по окнам архива: «63 %». Пусто — не читали либо окон нет. */
  wins: string | null;
  winsHint: string;
  /** Доля оборота ЭТОГО окна: «38 %». Пусто — оборота не было вовсе. */
  turnover: string | null;
  turnoverHint: string;
  /** «22 сут». Пусто — обхода не было ни у кого в группе. */
  depth: string | null;
  depthHint: string;
  /** «по одному обходу 4». Пусто, когда таких нет. */
  onePass: string;
  onePassHint: string;

  // --- средний блок: набрали в этом окне, по токену ----------------------
  assetID: string;
  /** Имя исхода. Пусто — справочника нет, вызывающий покажет короткий токен. */
  outcome: string;
  /** Короткий идентификатор токена — запасная подпись строки. */
  assetLabel: string;
  /** «+1 480.0», со знаком. Пусто — в группе нет кошельков. */
  net: string | null;
  /**
   * Те же шеры В ДЕНЬГАХ: `куплено − продано` по этому токену, со знаком.
   *
   * Именно это слагаемое и складывается в «Перевес»: он равен разности двух
   * таких чисел по исходам. Без него правая колонка — число ниоткуда: по шерам
   * группа может выглядеть набравшей оба исхода, а перевеса не иметь вовсе, и
   * увидеть, из чего он вышел, было нечем.
   *
   * Пусто — денег по токену не двигали (шеры пришли минтом пары либо строки нет).
   */
  netUsd: string | null;
  /** Пусто — выборка обрезана: роль НЕВЕРНА, а не менее точна. */
  taker: string | null;
  maker: string | null;
  /** «0.412». Пусто — этот токен группа не покупала вовсе. */
  avg: string | null;
  /** Вес средней: «·5 120». Пусто вместе со средней. */
  bought: string;
  avgHint: string;

  // --- правый блок: куда смотрят, в долларах -----------------------------
  /** Исход, в сторону которого перевес. Пусто — сводить нечем. */
  edgeOutcome: string;
  /** «$780», по модулю: знак вынесен в edgeOutcome. */
  edge: string | null;
  /** «из $3 210 по кошелькам». */
  edgeSpread: string;
  edgeHint: string;
}

/** Всё, что рисует блок, — одним расчётом по одному выбранному порогу. */
export interface CohortsView {
  /** Пусто — блока на экране нет вовсе. */
  lines: CohortLine[];
  floors: CohortFloor[];
  /** Ступень, ПО КОТОРОЙ посчитаны строки. */
  floor: CohortFloor | undefined;
  /** Основание — строками, ДО таблицы: кого посчитали и кого нет. */
  basis: string[];
  /** Покрытие выплат: текст и надо ли выделять. */
  paid: { text: string; alarm: boolean };
  /** Приписка ПОД таблицей: два окна, глубина, «набрано против перевеса». */
  note: string;
  /** Несошедшиеся проверки. Пусто — всё сошлось. */
  alarms: string[];
  /**
   * Подгруппы со знаком, в которых нет ни одного кошелька, — одной строкой под
   * таблицей, а не строками с прочерками: восемь подгрупп по два исхода дали бы
   * шестнадцать строк, из которых половина пустые. Ноль при этом остаётся
   * знанием — он назван, только не занимает две строки. Пусто — пустых нет.
   */
  empty: string;
  /** Почему блока нет, когда он скрыт намеренно. Пусто — молчать. */
  absent: string;
}

const EMPTY: CohortsView = {
  lines: [],
  floors: [],
  floor: undefined,
  basis: [],
  paid: { text: "", alarm: false },
  note: "",
  alarms: [],
  empty: "",
  absent: "",
};

export function cohortsView(
  p: ParticipantsStats | undefined,
  tokens: readonly Token[] | undefined,
  picked: string | undefined,
  against: boolean,
): CohortsView {
  // В режиме «против нас» блока нет, и это решение, а не отсутствие данных: его
  // вопрос — про рынок целиком, а на экране только наши контрагенты. Причину
  // называет сервер, здесь она просто доезжает до экрана.
  if (against || !p?.cohorts) {
    const absent = p?.cohorts_reason ?? "";
    return { ...EMPTY, absent };
  }

  const floors = floorsOf(p.cohorts.splits, p.cohorts.default_min_turnover_usd);
  const chosen = chooseFloor(floors, picked);
  const split = p.cohorts.splits.find(
    (s) => s.min_turnover_usd === chosen.floor?.usd,
  );
  if (!split || !chosen.floor) return { ...EMPTY, floors, absent: p.cohorts_reason ?? "" };

  const outcomes = outcomeMap(tokens);
  const groups = ordered(split.groups, p.cohorts);
  const windowTurnover = totalWindowTurnover(groups);
  const byOutcome = outcomeOrder(tokens);

  const lines: CohortLine[] = [];
  const empty: string[] = [];
  for (const g of groups) {
    // Пустая подгруппа со знаком — строкой под таблицей, а не двумя строками
    // прочерков. «Остальные» показываются всегда: их пустота — сама по себе
    // ответ («все обойдены и классифицированы»), и причины стоят именно там.
    if (g.wallets === 0 && signOf(g.kind) !== "rest") {
      empty.push(TITLES[g.kind]);
      continue;
    }
    const rows = ordersTokens(g.tokens, byOutcome);
    if (rows.length === 0) continue;
    rows.forEach((tok, i) => {
      lines.push(lineOfCohort(g, tok, i === 0, rows.length, outcomes, windowTurnover, p, chosen.floor!));
    });
  }

  const alarms = alarmsOf(p, groups, chosen.missing);
  return {
    lines,
    floors,
    floor: chosen.floor,
    basis: basisOf(p, groups, windowTurnover, chosen.floor),
    paid: paidOf(groups),
    note: noteOf(p, groups),
    alarms,
    empty: empty.length > 0 ? `Без кошельков: ${empty.join(", ")}.` : "",
    absent: "",
  };
}

// ---------------------------------------------------------------------------
// Лестница порогов
// ---------------------------------------------------------------------------

function floorsOf(
  splits: readonly ParticipantCohortSplit[],
  fallback: string,
): CohortFloor[] {
  return splits.map((s) => ({
    usd: s.min_turnover_usd,
    label: floorLabel(s.min_turnover_usd),
    fallback: s.min_turnover_usd === fallback,
  }));
}

/** «без порога», «$20 тыс.», «$500». */
function floorLabel(usd: string): string {
  const m = parse(usd);
  if (m === null || m === 0) return "без порога";
  if (m >= 1_000 * SCALE) {
    return `$${roundToFixed(m / 1000, 0)} тыс.`;
  }
  return formatDollars(usd);
}

/**
 * Какая ступень показана.
 *
 * Выбор оператора, которого сервер больше не считает, НЕ откатывается молча: он
 * возвращается словами. Молчаливый откат означал бы числа под чужим порогом при
 * нажатой кнопке своего.
 */
function chooseFloor(
  floors: CohortFloor[],
  picked: string | undefined,
): { floor: CohortFloor | undefined; missing: string } {
  if (floors.length === 0) return { floor: undefined, missing: "" };
  const byPick = picked ? floors.find((f) => f.usd === picked) : undefined;
  if (byPick) return { floor: byPick, missing: "" };
  const fallback = floors.find((f) => f.fallback) ?? floors[0]!;
  if (!picked) return { floor: fallback, missing: "" };
  return {
    floor: fallback,
    missing:
      `порог ${floorLabel(picked)} сервер больше не считает — ` +
      `показан ${fallback.label}`,
  };
}

// ---------------------------------------------------------------------------
// Строки
// ---------------------------------------------------------------------------

/**
 * Порядок исходов на экране — по outcome_index справочника рынка.
 *
 * Ответ отдаёт строки токенов упорядоченными по идентификатору: пакет не знает,
 * какой токен какой исход, и знать не должен. Но на экране сводка стоит НАД
 * таблицей, которая упорядочена по исходам, и два разных порядка в одной
 * карточке читаются как разные данные. Порядок задаёт тот, у кого есть
 * справочник, — то есть интерфейс.
 */
function outcomeOrder(tokens: readonly Token[] | undefined): Map<string, number> {
  const out = new Map<string, number>();
  for (const t of tokens ?? []) out.set(t.asset_id, t.outcome_index);
  return out;
}

function ordersTokens(
  rows: readonly ParticipantCohortToken[],
  byOutcome: Map<string, number>,
): ParticipantCohortToken[] {
  // Незнакомый токен уходит в конец, а не наверх: справочник неполон, и ставить
  // безымянную строку первой значило бы подписать блок тем, чего мы не знаем.
  const rank = (t: ParticipantCohortToken) =>
    byOutcome.get(t.asset_id) ?? Number.MAX_SAFE_INTEGER;
  return [...rows].sort((a, b) => rank(a) - rank(b) || a.asset_id.localeCompare(b.asset_id));
}

/** Порядок показа: плюсовые торгом, на выплатах, минусовые, остальные. */
/**
 * Подгруппы в порядке показа — ЕДИНСТВЕННЫЙ список имён на этой стороне.
 *
 * Экспортируется ради списка фильтра: он показывает ВСЕ подгруппы, а не только
 * встреченные в этом окне (список задан правилом, а не составом рынка), и
 * второй список имён разошёлся бы с этим ровно тогда, когда подгруппу добавят.
 */
export const COHORT_KINDS: readonly CohortKind[] = [
  "plus_taker",
  "plus_maker",
  "plus_mixed",
  "plus_payouts",
  "minus_taker",
  "minus_maker",
  "minus_mixed",
  "rest",
];

/**
 * Группы в порядке показа. Смешанные скрыты, когда границы роли РАВНЫ: тогда
 * их нет по построению (не выше X и не ниже X не оставляют ничего между), и
 * строка «ноль смешанных» была бы не знанием о рынке, а эхом настройки.
 * Решает настройка из ответа, а не ноль кошельков.
 */
function ordered(
  groups: readonly ParticipantCohort[],
  cohorts: ParticipantCohorts,
): ParticipantCohort[] {
  const noMixed = cohorts.maker_max_aggression === cohorts.taker_min_aggression;
  const out: ParticipantCohort[] = [];
  for (const kind of COHORT_KINDS) {
    if (noMixed && (kind === "plus_mixed" || kind === "minus_mixed")) continue;
    const g = groups.find((x) => x.kind === kind);
    if (g) out.push(g);
  }
  return out;
}

export const TITLES: Record<CohortKind, string> = {
  plus_taker: "плюсовые тейкеры",
  plus_maker: "плюсовые мейкеры",
  plus_mixed: "плюсовые смешанные",
  plus_payouts: "живут на выплатах",
  minus_taker: "минусовые тейкеры",
  minus_maker: "минусовые мейкеры",
  minus_mixed: "минусовые смешанные",
  rest: "остальные",
};

/**
 * Короткое имя для чипа в строке кошелька: «тейкер+», «выплаты», «мейкер−».
 * Знак — символом, роль — словом; у остальных чипа нет вовсе.
 */
export const SHORT_TITLES: Record<CohortKind, string> = {
  plus_taker: "тейкер+",
  plus_maker: "мейкер+",
  plus_mixed: "смеш.+",
  plus_payouts: "выплаты",
  minus_taker: "тейкер−",
  minus_maker: "мейкер−",
  minus_mixed: "смеш.−",
  rest: "",
};

/**
 * Определение под именем — с ПОРОГОМ из ответа, а не с константой: та же
 * причина, по которой лестница порогов оборота приезжает с сервера. Роль — доля
 * тейкерских сделок по всей глубине; «агрессия» здесь то же слово, что у тира.
 */
export function formulaOf(kind: CohortKind, cohorts: ParticipantCohorts): string {
  const maker = `агрессия ≤ ${shareLabel(cohorts.maker_max_aggression)}`;
  const taker = `агрессия ≥ ${shareLabel(cohorts.taker_min_aggression)}`;
  const mixed =
    `${shareLabel(cohorts.maker_max_aggression)} < агрессия < ` +
    shareLabel(cohorts.taker_min_aggression);
  switch (kind) {
    case "plus_taker":
      return `пнл > 0, ${taker}`;
    case "plus_maker":
      return `пнл > 0, ${maker}`;
    case "plus_mixed":
      return `пнл > 0, ${mixed}`;
    case "plus_payouts":
      return "пнл ≤ 0 < пнл + выплаты";
    case "minus_taker":
      return `пнл + выплаты < 0, ${taker}`;
    case "minus_maker":
      return `пнл + выплаты < 0, ${maker}`;
    case "minus_mixed":
      return `пнл + выплаты < 0, ${mixed}`;
    case "rest":
      return "не прошли отбор";
  }
}

/** «0.15» из строки денег: доля печатается двумя знаками, как в настройке. */
function shareLabel(usd: string): string {
  const m = parse(usd);
  if (m === null) return usd;
  return roundToFixed(m, 2);
}

function lineOfCohort(
  g: ParticipantCohort,
  tok: ParticipantCohortToken,
  head: boolean,
  span: number,
  outcomes: Map<string, string>,
  windowTurnover: Micro,
  p: ParticipantsStats,
  floor: CohortFloor,
): CohortLine {
  const empty = g.wallets === 0;
  const net = empty ? null : parse(tok.net);
  // Обрезанная выборка гасит роль ЦЕЛИКОМ — тем же правилом, что у строки
  // кошелька: мейкерская часть считается вычитанием двух по-разному обрезанных
  // выборок, и это не «менее точно», а неверно (замер: расхождение 137 %).
  const role = p.truncated || empty;
  const avg = avgOf(tok);
  const share = shareOf(g, windowTurnover);

  return {
    kind: g.kind,
    key: `${g.kind}:${tok.asset_id}`,
    head,
    span,
    title: TITLES[g.kind],
    formula: formulaOf(g.kind, p.cohorts!),
    titleHint: titleHintOf(g, floor, p),
    reasons: head ? reasonsOf(g, floor) : "",
    wallets: g.wallets,
    roles: head ? rolesOf(g) : "",
    rolesHint:
      "роль кошельков этой подгруппы по доле тейкерских сделок за всю глубину. У живущих " +
      "на выплатах роль в имя не зашита: возврат тейкеру и возврат мейкеру — разные " +
      "программы, и кто из них кормит кошелёк, видно только здесь",
    guessing: head ? guessingOf(g, p) : "",
    guessingHint: guessingHintOf(p),
    wins: head ? winsOf(g) : null,
    winsHint: winsHintOf(g),
    turnover: head ? share : null,
    turnoverHint:
      "доля группы в обороте ЭТОГО окна (куплено плюс продано, в долларах), а не в её " +
      "собственном обороте на коротких рынках. Проценты по всем группам дают сто",
    depth: head ? depthOf(g) : null,
    depthHint: depthHintOf(g),
    onePass: head ? onePassOf(g, p) : "",
    onePassHint:
      `кошельки, у которых свод не глубже ${p.cohorts?.one_pass_days ?? 0} сут, то есть ` +
      "ровно один проход обходчика: их знак — это один день торгов, а не история",
    assetID: tok.asset_id,
    outcome: outcomes.get(tok.asset_id) ?? "",
    assetLabel: shortId(tok.asset_id, 8),
    net: net === null ? null : signedShares(net),
    netUsd: netUsdOf(tok),
    taker: role ? null : signedShares(parse(tok.taker) ?? 0),
    maker: role ? null : signedShares(parse(tok.maker) ?? 0),
    avg: avg === null ? null : roundToFixed(avg, 3),
    bought: avg === null ? "" : `·${formatShares(tok.bought)}`,
    avgHint:
      "средняя цена ПОКУПОК этого токена кошельками группы, взвешенная объёмом. Это НЕ " +
      "цена входа в показанное «Набрано»: рядом стоит, сколько куплено всего, и отношение " +
      "двух чисел отвечает, набирали группа позицию или крутила оборот",
    edgeOutcome: head ? edgeOutcomeOf(g, outcomes) : "",
    edge: head ? edgeOf(g) : null,
    edgeSpread: head ? edgeSpreadOf(g) : "",
    edgeHint: EDGE_HINT,
  };
}

const EDGE_HINT =
  "перевес в ДОЛЛАРАХ ВЛОЖЕННОГО: (вложено в первый исход − изъято из него) минус то же " +
  "по второму. Отвечает на вопрос «есть ли у группы мнение о направлении», а колонки шеров " +
  "— на вопрос «что именно она держит». Расходятся они законно: кошелёк, набравший ОБА " +
  "исхода, по шерам выглядит набравшим дважды, а перевеса не даёт вовсе. Рядом — сумма " +
  "перевесов по кошелькам: если она много больше, участники группы смотрят в разные " +
  "стороны и гасят друг друга. Отличить это от «каждый держит оба исхода» по двум числам " +
  "нельзя, и третьего в ответе нет";

/**
 * Те же шеры в ДЕНЬГАХ: `bought_usd − sold_usd`, со знаком.
 *
 * # Почему именно эта разность, а не «шеры × средняя цена»
 *
 * Потому что перевес в правой колонке складывается ровно из неё: он равен
 * разности двух таких чисел по исходам. Умножение шеров на среднюю цену дало бы
 * похожее, но ДРУГОЕ число — средняя считается только по покупкам, а нетто
 * бывает набрано и продажей второго исхода, — и колонки перестали бы сходиться.
 * Подпись, не сходящаяся с числом, которое она объясняет, хуже её отсутствия.
 *
 * Знак значим и остаётся: минус означает, что из токена деньги ИЗЪЯЛИ, продав
 * больше, чем купили.
 *
 * null — денег по токену не двигали вовсе: шеры пришли минтом пары либо строки
 * в ответе нет. Ноль здесь был бы утверждением «двигали ровно на ноль».
 */
function netUsdOf(tok: ParticipantCohortToken): string | null {
  const bought = parse(tok.bought_usd);
  const sold = parse(tok.sold_usd);
  if (bought === null && sold === null) return null;
  const net = (bought ?? 0) - (sold ?? 0);
  if (net === 0) return null;
  const sign = net > 0 ? "+" : "−";
  return `${sign}${formatDollars(fixed(Math.abs(net)))}`;
}

/** Средняя цена покупок токена группой: bought_usd / bought. */
function avgOf(tok: ParticipantCohortToken): Micro | null {
  const usd = parse(tok.bought_usd);
  const shares = parse(tok.bought);
  // Ноль здесь — не «покупали по нулю», а «не покупали вовсе»: группа набрала
  // лонг, продавая второй исход, либо шеры пришли минтом.
  if (usd === null || shares === null || shares <= 0) return null;
  return div(usd, shares);
}

/**
 * Оборот группы В ЭТОМ ОКНЕ: куплено плюс продано, в долларах.
 *
 * Именно оконный, а не turnover_usd из ответа: тот — оборот кошельков на ВСЕХ
 * коротких рынках за всю глубину, то есть основание классификации. Подставь его
 * в колонку «доля оборота», и проценты отвечали бы на вопрос про другой период
 * и другие рынки.
 */
function windowTurnoverOf(g: ParticipantCohort): Micro {
  let total: Micro = 0;
  for (const tok of g.tokens) {
    const sum = add(parse(tok.bought_usd) ?? 0, parse(tok.sold_usd) ?? 0);
    if (sum === null) return total;
    const next = add(total, sum);
    if (next === null) return total;
    total = next;
  }
  return total;
}

function totalWindowTurnover(groups: readonly ParticipantCohort[]): Micro {
  let total: Micro = 0;
  for (const g of groups) {
    const next = add(total, windowTurnoverOf(g));
    if (next === null) return total;
    total = next;
  }
  return total;
}

function shareOf(g: ParticipantCohort, total: Micro): string | null {
  if (total <= 0) return null;
  const ratio = div(windowTurnoverOf(g), total);
  if (ratio === null) return null;
  const percent = mul(ratio, 100 * SCALE);
  if (percent === null) return null;
  return `${roundToFixed(percent, 0)} %`;
}

function depthOf(g: ParticipantCohort): string | null {
  if (g.known === 0 || g.days_median === 0) return null;
  return `${g.days_median} сут`;
}

function depthHintOf(g: ParticipantCohort): string {
  if (g.known === 0) return "обхода не было ни у одного кошелька группы";
  return (
    `медиана по ${g.known} ${plural(g.known, "кошельку", "кошелькам", "кошелькам")} группы, ` +
    `у которых обход был (от ${g.days_min} до ${g.days_max} сут); ` +
    `у остальных ${g.wallets - g.known} обхода не было вовсе. ` +
    "Медиана, а не среднее: старожилы тянут среднее вверх и прячут хвост однодневок"
  );
}

function onePassOf(g: ParticipantCohort, p: ParticipantsStats): string {
  if (g.one_pass === 0) return "";
  const days = p.cohorts?.one_pass_days ?? 0;
  return `по одному обходу ${g.one_pass}${days > 0 ? ` (≤${days} сут)` : ""}`;
}

/**
 * Разбор «остальных» — числами, а не в подсказке.
 *
 * «Не обходили» и «оборот ниже порога» разделяются обязательно: первое —
 * НЕЗНАНИЕ (кошелёк может оказаться кем угодно), второе — знание («посмотрели,
 * мелкий»). Ровно то различие, ради которого в ADR-0007 unknown отделён от noise
 * и ради которого вся эта панель ставит прочерк вместо нуля.
 */
function reasonsOf(g: ParticipantCohort, floor: CohortFloor): string {
  const parts: string[] = [];
  if (g.unwalked > 0) parts.push(`не обходили ${g.unwalked}`);
  if (g.below_turnover > 0) {
    parts.push(`оборот ниже ${floor.label} — ${g.below_turnover}`);
  }
  if (g.unusable_days > 0) parts.push(`непригодные сутки ${g.unusable_days}`);
  if (g.truncated_days > 0) parts.push(`урезанные сутки ${g.truncated_days}`);
  if (g.ours > 0) parts.push(`наш кошелёк ${g.ours}`);
  if (g.flat > 0) parts.push(`итог ровно ноль ${g.flat}`);
  return parts.join(" · ");
}

/**
 * Роли — только у живущих на выплатах. У ролевых подгрупп они тривиальны (все
 * одной роли — это сказано именем), у остальных отсутствуют вовсе.
 */
function rolesOf(g: ParticipantCohort): string {
  if (g.kind !== "plus_payouts" || !g.roles) return "";
  const parts: string[] = [];
  if (g.roles.taker > 0) parts.push(`тейкеров ${g.roles.taker}`);
  if (g.roles.maker > 0) parts.push(`мейкеров ${g.roles.maker}`);
  if (g.roles.mixed > 0) parts.push(`смешанных ${g.roles.mixed}`);
  return parts.join(" · ");
}

/**
 * Доля побед по окнам архива: Σпобед / Σокон по кошелькам группы.
 *
 * null — не читали (поля нет) либо окон не было: и то и другое — прочерк, а не
 * «0 %». Ноль процентов означал бы «проиграли все окна», а этого никто не мерил.
 */
function winsOf(g: ParticipantCohort): string | null {
  const w = g.wins;
  if (!w || w.windows <= 0) return null;
  return `${Math.round((100 * w.won) / w.windows)} %`;
}

function winsHintOf(g: ParticipantCohort): string {
  const w = g.wins;
  const head = !w
    ? "победы не прочитаны. "
    : w.windows <= 0
      ? "ни одного кошелька группы архив окон не застал. "
      : `по ${w.wallets} ${plural(w.wallets, "кошельку", "кошелькам", "кошелькам")} группы, ` +
        `${formatShares(fixed(w.windows * SCALE))} ${plural(w.windows, "окно", "окна", "окон")} архива; ` +
        `у остальных ${g.wallets - w.wallets} окон в архиве нет. `;
  return (
    head +
    "Победа — знак нетто кошелька на срезе за 120 с до конца окна совпал с исходом; " +
    "идеально захеджированный получает ноль. Считаются только окна НАШЕГО архива, а не " +
    "вся история кошелька, и через выкат 2026-08-28 ряд ступенчатый"
  );
}

/**
 * «угадывают ≥60 %: 12 из 31 (≥20 окон)» — знаменатель НЕ кошельки группы, а
 * те, у кого окон хватает: два окна из двух — не мнение, и такие в знаменатель
 * не входят. Пусто — судить некого либо не читали.
 */
function guessingOf(g: ParticipantCohort, p: ParticipantsStats): string {
  const w = g.wins;
  const c = p.cohorts;
  if (!w || !c || w.judged <= 0 || signOf(g.kind) === "rest") return "";
  return (
    `угадывают ≥${shareLabel(c.win_min_share)}: ${w.guessing} из ${w.judged}` +
    ` (≥${c.win_min_windows} окон)`
  );
}

function guessingHintOf(p: ParticipantsStats): string {
  const c = p.cohorts;
  return (
    `кошельки группы, у которых окон в нашем архиве не меньше ${c?.win_min_windows ?? 0} и ` +
    `доля выигранных из них не ниже ${shareLabel(c?.win_min_share ?? "0")}. Знаменатель — ` +
    "кошельки с достаточным числом окон, а не вся группа: пять из пяти — это не мнение"
  );
}

function titleHintOf(
  g: ParticipantCohort,
  floor: CohortFloor,
  p: ParticipantsStats,
): string {
  const window = p.cohorts?.window === "all" ? "всю накопленную глубину" : "окно классификации";
  if (g.kind === "rest") {
    return (
      "всё, что не прошло отбор, и причины у этого РАЗНЫЕ. «Не обходили» — это НЕЗНАНИЕ: " +
      "оборот кошелька ниже порога наблюдения либо очередь до него не дошла, и про него мы " +
      "не знаем ничего. Это не «мелкий» и не «нулевой». Числа стоят под названием"
    );
  }
  const sign = signOf(g.kind) === "plus" ? "больше" : "меньше";
  const how =
    g.kind === "plus_payouts"
      ? "при этом сам пнл не больше нуля — знак дали только выплаты. "
      : isTrading(g.kind)
        ? "при этом сам пнл больше нуля — плюсовые и без выплат. "
        : "";
  return (
    `за ${window} кошелька пнл (за вычетом тейкерской комиссии) ПЛЮС выплаты биржи ` +
    `${sign} нуля; оборот за тот же период — от ${floor.label}. ${how}` +
    "Роль — доля тейкерских сделок за ту же глубину против порогов из формулы. " +
    "Это НЕ класс кита на карточке сигнала: там отношение пнл к вложенному с порогом ±10 %, " +
    "за неделю и БЕЗ выплат. Одно из другого не следует ни в какую сторону"
  );
}

function edgeOutcomeOf(g: ParticipantCohort, outcomes: Map<string, string>): string {
  const e = g.edge;
  if (!e) return "";
  const usd = parse(e.usd);
  if (usd === null || usd === 0) return "";
  const asset = usd > 0 ? e.first_asset_id : e.second_asset_id;
  return outcomes.get(asset) ?? shortId(asset, 8);
}

function edgeOf(g: ParticipantCohort): string | null {
  const e = g.edge;
  if (!e) return null;
  const usd = parse(e.usd);
  if (usd === null) return null;
  return formatDollars(fixed(Math.abs(usd)));
}

function edgeSpreadOf(g: ParticipantCohort): string {
  const e = g.edge;
  if (!e) return "";
  const abs = parse(e.abs_usd);
  if (abs === null) return "";
  return `из ${formatDollars(fixed(abs))} по кошелькам`;
}

// ---------------------------------------------------------------------------
// Тексты
// ---------------------------------------------------------------------------

/**
 * Основание — ДО чисел, а не после.
 *
 * «34 из 248» надо прочитать раньше, чем «+1 480 Up»: половина кошельков рынка
 * не обойдена, и без основания сводка читается как утверждение о рынке, тогда
 * как она утверждение о меньшинстве. Обе доли обязательны — по кошелькам и по
 * обороту: 14 % кошельков звучит ничтожно, 71 % оборота — решающе, и правда в
 * обоих.
 */
function basisOf(
  p: ParticipantsStats,
  groups: readonly ParticipantCohort[],
  windowTurnover: Micro,
  floor: CohortFloor,
): string[] {
  const rest = groups.find((g) => g.kind === "rest");
  const signed = groups.filter((g) => signOf(g.kind) !== "rest");
  if (!rest || signed.length === 0) return [];

  const classified = walletsOf(signed);
  const out: string[] = [];
  if (classified === 0) {
    out.push(
      `В группы не попал ни один кошелёк из ${p.wallets}: ` +
        (rest.unwalked === rest.wallets
          ? "обхода не было ни у одного"
          : `не обходили ${rest.unwalked}, оборот ниже ${floor.label} — ${rest.below_turnover}`) +
        ".",
    );
    return out;
  }

  const shareUSD = shareOfWallets(signed, windowTurnover);
  out.push(
    `В группы попали ${classified} ${plural(classified, "кошелёк", "кошелька", "кошельков")} ` +
      `из ${p.wallets}` +
      (shareUSD ? ` — на них ${shareUSD} оборота этого окна` : "") +
      ".",
  );
  // Плюсовые — торгом и на выплатах: то самое «на чём группа живёт», прежде
  // стоявшее числом «и без выплат N из M» под именем группы.
  const plus = walletsOf(signed.filter((g) => signOf(g.kind) === "plus"));
  if (plus > 0) {
    const trading = walletsOf(signed.filter((g) => isTrading(g.kind)));
    out.push(`Плюсовых ${plus}: торгом ${trading}, на выплатах ${plus - trading}.`);
  }
  if (rest.wallets > 0) {
    const reasons = reasonsOf(rest, floor);
    out.push(
      `Остальные ${rest.wallets}: ${reasons || "причина не названа"}.`,
    );
  }
  return out;
}

function walletsOf(groups: readonly ParticipantCohort[]): number {
  return groups.reduce((n, g) => n + g.wallets, 0);
}

function shareOfWallets(
  signed: readonly ParticipantCohort[],
  total: Micro,
): string | null {
  if (total <= 0) return null;
  let both: Micro = 0;
  for (const g of signed) {
    const next = add(both, windowTurnoverOf(g));
    if (next === null) return null;
    both = next;
  }
  const ratio = div(both, total);
  if (ratio === null) return null;
  const percent = mul(ratio, 100 * SCALE);
  if (percent === null) return null;
  return `${roundToFixed(percent, 0)} %`;
}

/**
 * Покрытие выплат — числом и с НАПРАВЛЕНИЕМ ошибки.
 *
 * Выплаты входят в определение группы, а спрошены они пока у малой части: замер
 * 2026-08-19 дал 145 кошельков из 1484, то есть 9.8 %. Молчать об этом нельзя,
 * но и пугать «данные неполны» незачем: переводы биржи ВХОДЯЩИЕ, значит их
 * приход может двигать знак только вверх — плюсовых показано не больше, чем их
 * есть, а минусовых не меньше. Это проверяемое утверждение, а не оговорка.
 *
 * При полном покрытии строка ИСЧЕЗАЕТ, а не заменяется зелёной «всё учтено»:
 * строка, которая всегда на экране, перестаёт нести информацию.
 */
function paidOf(groups: readonly ParticipantCohort[]): { text: string; alarm: boolean } {
  const signed = groups.filter((g) => signOf(g.kind) !== "rest");
  if (signed.length === 0) return { text: "", alarm: false };

  const classified = walletsOf(signed);
  const asked = signed.reduce((n, g) => n + g.incentives_asked, 0);
  if (classified === 0 || asked >= classified) return { text: "", alarm: false };

  const rest = classified - asked;
  return {
    text:
      `Выплаты биржи спрошены у ${asked} ${plural(asked, "кошелька", "кошельков", "кошельков")} ` +
      `из ${classified}: у остальных ${rest} в знак вошёл только пнл, и с приходом выплат ` +
      "плюсовых станет только больше — переводы биржи входящие, а новые плюсовые придут " +
      "в «живут на выплатах».",
    alarm: true,
  };
}

/**
 * Приписка под блоком: два окна названы РАЗНЫМИ значениями в одной фразе.
 *
 * Подставляется окно ИЗ ОТВЕТА, а не из выбора оператора: пока новый свод едет,
 * на экране числа за прежнее окно, и подписать их новым значило бы соврать на
 * полсекунды ровно в том поле, ради честности которого переключатель заведён.
 */
function noteOf(p: ParticipantsStats, groups: readonly ParticipantCohort[]): string {
  const spread = depthSpread(groups);
  const parts: string[] = [
    "Знак и роль — за ВСЮ НАКОПЛЕННУЮ ГЛУБИНУ кошелька" +
      (spread ? ` (${spread})` : "") +
      ": знак — пнл за вычетом тейкерской комиссии плюс выплаты биржи, роль — доля " +
      "тейкерских сделок. «Живут на выплатах» — те, у кого знак дали только выплаты.",
  ];
  if (p.flow_window === "all") {
    // Фраза «это НЕ окно справа» стала бы ложью, а ложная оговорка хуже её
    // отсутствия: её запомнят и перестанут проверять.
    parts.push(
      "Сейчас в разбивке по кошелькам выбрано то же окно («всё»), поэтому периоды совпали — " +
        "но это совпадение выбора, а не связь: тот переключатель группы не меняет.",
    );
  } else {
    parts.push(
      `Это НЕ окно «на коротких рынках» из разбивки по кошелькам — там сейчас ` +
        `${windowLabel(p)}, и переключатель на группы не влияет.`,
    );
  }
  parts.push(
    "Глубина у каждого кошелька своя: сутки старше срока хранения убирает уборщик, а за " +
      "один обход глубже пары суток не заглянуть — поэтому у части кошельков за знаком " +
      "стоит один день торгов, и такие посчитаны отдельно под названием группы.",
  );
  parts.push(
    "«Набрано» и «Перевес» отвечают на разные вопросы и расходятся законно: кошелёк, " +
      "набравший ОБА исхода, по шерам выглядит набравшим дважды, а перевеса не даёт вовсе — " +
      "мнения о направлении у него нет.",
  );
  if (p.truncated) {
    parts.push(
      "Выборка обрезана потолком источника: суммы групп неполны — старые сделки в них не " +
        "вошли, — а колонки роли скрыты: на обрезанной истории роль неверна, а не " +
        "приблизительна.",
    );
  }
  return parts.join(" ");
}

function windowLabel(p: ParticipantsStats): string {
  switch (p.flow_window) {
    case "today":
      return "сегодня";
    case "3d":
      return "3 суток";
    case "7d":
      return "7 суток";
    case "30d":
      return "30 суток";
    case "all":
      return "вся глубина";
    default:
      return "окно по умолчанию";
  }
}

function depthSpread(groups: readonly ParticipantCohort[]): string {
  const known = groups.filter((g) => g.known > 0);
  if (known.length === 0) return "";
  const min = Math.min(...known.map((g) => g.days_min));
  const max = Math.max(...known.map((g) => g.days_max));
  const medians = known.map((g) => g.days_median).sort((a, b) => a - b);
  const median = medians[Math.floor(medians.length / 2)] ?? 0;
  return `медиана ${median} сут, от ${min} до ${max}`;
}

/**
 * Проверки, которые клиент может сделать сам.
 *
 * Первая — единственный способ поймать самый дорогой дефект этого блока: агрегат
 * по сотне показанных строк вместо всех кошельков рынка.
 *
 * Вторая — независимая, и она НЕ «сумма нетто равна нулю». Нулю она не равна и
 * равняться не должна: на коротком рынке большинство матчей комплементарные
 * (замер marketflow: 1132 и 1226 из 1302 и 1444), а комплементарный матч — это
 * МИНТ ПАРЫ, где обе стороны покупают РАЗНЫЕ токены. Такая сделка добавляет
 * плюс и к Up, и к Down, и Σ нетто по токену равна числу отчеканенных пар.
 * Проверено на живом рынке 2026-08-20: Σ по Up и Σ по Down совпали до шестого
 * знака (17099.965189 против 17099.965189) при сумме, далёкой от нуля.
 *
 * Отсюда настоящий инвариант: суммы нетто ПО ДВУМ ИСХОДАМ равны между собой —
 * минт создаёт по одному шеру каждого. Потеряйся кошелёк в раскладке, равенство
 * разойдётся. Проверка молчит на обрезанной и несошедшейся выборке: там равенства
 * и не должно быть.
 */
function alarmsOf(
  p: ParticipantsStats,
  groups: readonly ParticipantCohort[],
  missing: string,
): string[] {
  const out: string[] = [];
  if (missing) out.push(missing);

  const counted = groups.reduce((sum, g) => sum + g.wallets, 0);
  if (counted !== p.wallets) {
    out.push(
      `сводка посчитана по ${counted} ${plural(counted, "кошельку", "кошелькам", "кошелькам")} ` +
        `из ${p.wallets}: суммы групп неполны`,
    );
  }
  if (!p.truncated && p.balanced) {
    const gap = mintGap(groups);
    if (gap && Math.abs(gap.diff) > gap.allowed) {
      out.push(
        "суммы нетто по двум исходам разошлись " +
          `(${signedShares(gap.diff)}): минт создаёт по шеру каждого, ` +
          "значит часть кошельков рынка в сводку не попала",
      );
    }
  }
  return out;
}

/**
 * Насколько разошлись суммы нетто по двум исходам — и сколько им позволено.
 *
 * Допуск зеркалит серверный (Σ/1000, но не меньше сотой доли share) и по той же
 * причине: штатный матч балансируется до последнего знака, а пыль биржи на живом
 * рынке составила 9.4e-8 от суммы. Настоящая поломка выглядит иначе — 137 %.
 *
 * Дублирование допуска названо вслух: держать его в одном месте на двух языках
 * нечем, а расхождение проверок хуже расхождения констант — на живом рынке
 * стороннюю тревогу перестают читать вместе с настоящей.
 */
function mintGap(
  groups: readonly ParticipantCohort[],
): { diff: Micro; allowed: Micro } | null {
  const assets = groups[0]?.tokens.map((t) => t.asset_id) ?? [];
  // Ровно два исхода: на небинарном рынке минт устроен иначе, и равенство
  // двух сумм там ничего не значит.
  if (assets.length !== 2) return null;

  const nets: Micro[] = [0, 0];
  let gross: Micro = 0;
  for (const g of groups) {
    assets.forEach((asset, i) => {
      const tok = g.tokens.find((t) => t.asset_id === asset);
      if (!tok) return;
      const n = add(nets[i]!, parse(tok.net) ?? 0);
      if (n !== null) nets[i] = n;
      const turn = add(parse(tok.bought) ?? 0, parse(tok.sold) ?? 0);
      if (turn === null) return;
      const gr = add(gross, turn);
      if (gr !== null) gross = gr;
    });
  }
  return {
    diff: nets[0]! - nets[1]!,
    allowed: Math.max(Math.floor(gross / 1000), 10_000),
  };
}

// ---------------------------------------------------------------------------
// Формат
// ---------------------------------------------------------------------------

/**
 * Число со знаком, ВКЛЮЧАЯ плюс.
 *
 * Плюс обязателен: в таблице ниже колонка «Нетто» безнаковая — знак вынесен в
 * «Исход», — и колонка без плюса читалась бы как та же безнаковая величина.
 * Минус типографский, как везде в панели.
 */
function signedShares(m: Micro): string {
  const body = formatShares(fixed(Math.abs(m)));
  if (m > 0) return `+${body}`;
  if (m < 0) return `−${body}`;
  return body;
}

/** Цвет знаковой величины: КРАСИТСЯ ТОЛЬКО МИНУС. */
export function sharesColor(value: string | null): string | undefined {
  if (value && value.startsWith("−")) return "var(--color-sell)";
  // Зелёного на плюсе нет намеренно: набранное — не прибыль, и цвет прибыли
  // приписал бы группе оценку, которой в числе нет.
  return undefined;
}
